(() => {
  if (window.__lumeaMoonBundleUnitVariantsInstalled) return;
  window.__lumeaMoonBundleUnitVariantsInstalled = true;

  const originalFetch = window.fetch;
  const OriginalXMLHttpRequest = window.XMLHttpRequest;
  let scheduleNormalization = () => {};

  const isMoonBundleProduct = (properties) => {
    if (!properties || typeof properties.__moonbundle !== 'string') return false;

    try {
      return JSON.parse(properties.__moonbundle).type === 'bundle';
    } catch (_error) {
      return false;
    }
  };

  const createUnitId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  const separateBundleUnits = (payload) => {
    if (!Array.isArray(payload?.items)) return false;

    let changed = false;
    const separatedItems = [];

    payload.items.forEach((item) => {
      const quantity = Number(item?.quantity || 1);

      if (quantity <= 1 || !isMoonBundleProduct(item.properties)) {
        separatedItems.push(item);
        return;
      }

      changed = true;
      const groupId = createUnitId();

      for (let unit = 1; unit <= quantity; unit += 1) {
        separatedItems.push({
          ...item,
          quantity: 1,
          properties: {
            ...item.properties,
            _lumea_bundle_unit: `${groupId}-${unit}`,
          },
        });
      }
    });

    if (changed) payload.items = separatedItems;
    return changed;
  };

  window.fetch = function lumeaMoonBundleFetch(input, init) {
    let shouldNormalizeAfterRequest = false;

    try {
      const url = typeof input === 'string' ? input : input?.url || String(input);
      const method = String(init?.method || input?.method || 'GET').toUpperCase();
      const isCartAdd = /\/cart\/add(?:\.js)?(?:[?#]|$)/.test(url);
      shouldNormalizeAfterRequest = method === 'POST' && isCartAdd;

      if (method === 'POST' && isCartAdd && typeof init?.body === 'string') {
        const payload = JSON.parse(init.body);

        if (separateBundleUnits(payload)) {
          init = { ...init, body: JSON.stringify(payload) };
        }
      }
    } catch (_error) {
      // Keep the original request untouched if it is not a MoonBundle JSON request.
    }

    const responsePromise = originalFetch.call(this, input, init);

    if (!shouldNormalizeAfterRequest) return responsePromise;

    return responsePromise.then((response) => {
      if (response?.ok) scheduleNormalization(75);
      return response;
    });
  };

  const cartRoute = (path) => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/?$/, '/')}${path}`;
  };

  const requestCart = (path, method = 'GET', body = null) =>
    new Promise((resolve, reject) => {
      const request = new OriginalXMLHttpRequest();
      request.open(method, cartRoute(path), true);
      request.setRequestHeader('Accept', 'application/json');

      if (body) request.setRequestHeader('Content-Type', 'application/json');

      request.onload = () => {
        let response;

        try {
          response = JSON.parse(request.responseText);
        } catch (_error) {
          response = null;
        }

        if (request.status >= 200 && request.status < 300) resolve(response);
        else reject(new Error(response?.description || `Cart request failed (${request.status})`));
      };

      request.onerror = () => reject(new Error('Cart request failed'));
      request.send(body ? JSON.stringify(body) : null);
    });

  let bundleLineKeys = new Set();

  let visualCartLineOrder = [];

  const getCartLineIdentity = (item) => {
    const unitId = item?.properties?._lumea_bundle_unit;
    return unitId ? `unit:${unitId}` : `key:${item?.key || ''}`;
  };

  const orderCartItems = (cart, preferredOrder) => {
    if (!Array.isArray(cart?.items) || !Array.isArray(preferredOrder)) return cart;

    const positions = new Map(preferredOrder.map((identity, index) => [identity, index]));
    const originalPositions = new Map(
      cart.items.map((item, index) => [getCartLineIdentity(item), index])
    );

    cart.items.sort((first, second) => {
      const firstIdentity = getCartLineIdentity(first);
      const secondIdentity = getCartLineIdentity(second);
      const firstPosition = positions.has(firstIdentity)
        ? positions.get(firstIdentity)
        : preferredOrder.length + originalPositions.get(firstIdentity);
      const secondPosition = positions.has(secondIdentity)
        ? positions.get(secondIdentity)
        : preferredOrder.length + originalPositions.get(secondIdentity);

      return firstPosition - secondPosition;
    });

    return cart;
  };

  const getDisplayedCartLineKeys = (drawer) => {
    const keys = [];

    drawer?.querySelectorAll('select.moonbundle-select__select').forEach((select) => {
      const key = select.dataset.itemKey;
      if (key && !keys.includes(key)) keys.push(key);
    });

    return keys;
  };

  const getPreferredCartLineOrder = (
    cart,
    displayedLineKeys,
    replacedItem,
    replacement
  ) => {
    const itemsByKey = new Map((cart?.items || []).map((item) => [item.key, item]));
    const displayedOrder = displayedLineKeys
      .map((key) => itemsByKey.get(key))
      .filter(Boolean)
      .map(getCartLineIdentity);
    const fallbackOrder = visualCartLineOrder.length
      ? [...visualCartLineOrder]
      : (cart?.items || []).map(getCartLineIdentity);
    const currentOrder = displayedOrder.length ? displayedOrder : fallbackOrder;
    const currentIdentity = getCartLineIdentity(replacedItem);
    const replacementIdentity = getCartLineIdentity(replacement);

    (cart?.items || []).forEach((item) => {
      const identity = getCartLineIdentity(item);
      if (!currentOrder.includes(identity)) currentOrder.push(identity);
    });

    return currentOrder.map((identity) =>
      identity === currentIdentity ? replacementIdentity : identity
    );
  };

  const reconcileCartLineOrder = (cart, preferredOrder = null) => {
    if (!Array.isArray(cart?.items)) return cart;

    const nativeOrder = cart.items.map(getCartLineIdentity);
    const currentIdentities = new Set(nativeOrder);
    const sourceOrder = preferredOrder || visualCartLineOrder;
    const retainedOrder = sourceOrder.filter((identity) =>
      currentIdentities.has(identity)
    );
    const stableOrder = [
      ...retainedOrder,
      ...nativeOrder.filter((identity) => !retainedOrder.includes(identity)),
    ];

    if (preferredOrder || retainedOrder.length > 0) {
      orderCartItems(cart, stableOrder);
    }

    visualCartLineOrder = cart.items.map(getCartLineIdentity);
    return cart;
  };

  const updateBundleLineKeys = (cart) => {
    bundleLineKeys = new Set(
      (cart?.items || [])
        .filter((item) => isMoonBundleProduct(item.properties))
        .map((item) => item.key)
    );
  };

  const installDrawerOrderGuard = (drawer) => {
    if (
      !drawer ||
      drawer.__lumeaCartOrderGuardInstalled ||
      typeof drawer.applyCartToDrawer !== 'function'
    ) {
      return;
    }

    const originalApplyCartToDrawer = drawer.applyCartToDrawer.bind(drawer);
    drawer.applyCartToDrawer = (cart) => {
      reconcileCartLineOrder(cart);
      updateBundleLineKeys(cart);
      return originalApplyCartToDrawer(cart);
    };
    drawer.__lumeaCartOrderGuardInstalled = true;
  };

  const refreshMoonBundleDrawer = (cart, preferredOrder = null) => {
    reconcileCartLineOrder(cart, preferredOrder);
    updateBundleLineKeys(cart);
    const drawer = document.querySelector('moonbundle-cart-drawer-block');

    if (drawer && typeof drawer.applyCartToDrawer === 'function') {
      installDrawerOrderGuard(drawer);
      drawer.applyCartToDrawer(cart);
      drawer.triggerCartUpdate?.('lumea-unit-normalizer', { skipLoadCart: true });
      window.requestAnimationFrame(() => drawer._ensureCartLineEventDelegation?.());
      return;
    }

    window.dispatchEvent(
      new CustomEvent('moonbundle-cart-updated', {
        detail: { cart, caller: 'lumea-unit-normalizer', skipLoadCart: false },
      })
    );
  };

  let normalizationPromise = null;
  let normalizationQueued = false;

  const normalizeExistingCart = () => {
    if (normalizationPromise) {
      normalizationQueued = true;
      return normalizationPromise;
    }

    normalizationQueued = false;

    normalizationPromise = (async () => {
      let cart;

      for (let pass = 0; pass < 10; pass += 1) {
        cart = await requestCart('cart.js');
        const groupedItem = cart?.items?.find(
          (item) => Number(item.quantity) > 1 && isMoonBundleProduct(item.properties)
        );

        if (!groupedItem) break;

        const quantity = Number(groupedItem.quantity);
        const groupId = createUnitId();
        const additions = [];

        for (let unit = 2; unit <= quantity; unit += 1) {
          const addition = {
            id: groupedItem.variant_id || groupedItem.id,
            quantity: 1,
            properties: {
              ...groupedItem.properties,
              _lumea_bundle_unit: `${groupId}-${unit}`,
            },
          };
          const sellingPlanId = groupedItem.selling_plan_allocation?.selling_plan?.id;

          if (sellingPlanId) addition.selling_plan = sellingPlanId;
          additions.push(addition);
        }

        await requestCart('cart/change.js', 'POST', {
          id: groupedItem.key,
          quantity: 1,
        });

        try {
          await requestCart('cart/add.js', 'POST', { items: additions });
        } catch (error) {
          await requestCart('cart/change.js', 'POST', {
            id: groupedItem.key,
            quantity,
          }).catch(() => {});
          throw error;
        }
      }

      cart = await requestCart('cart.js');
      refreshMoonBundleDrawer(cart);
      return cart;
    })()
      .catch((error) => {
        console.error('[Lumea] Unable to separate MoonBundle cart units:', error);
      })
      .finally(() => {
        normalizationPromise = null;
        if (normalizationQueued) {
          normalizationQueued = false;
          scheduleNormalization(0);
        }
      });

    return normalizationPromise;
  };

  const productRequests = new Map();

  const getProduct = (item) => {
    const productPath = item.handle
      ? `products/${item.handle}.js`
      : `${item.url.split('?')[0].replace(/^\//, '')}.js`;

    if (!productRequests.has(productPath)) {
      productRequests.set(
        productPath,
        requestCart(productPath).catch((error) => {
          productRequests.delete(productPath);
          throw error;
        })
      );
    }

    return productRequests.get(productPath);
  };

  const setVariantControlsBusy = (drawer, busy) => {
    if (!drawer) return;
    drawer.toggleAttribute('data-lumea-variant-change-pending', busy);
    drawer.setAttribute('aria-busy', String(busy));
  };

  const installBusyStyles = () => {
    if (document.getElementById('lumea-moonbundle-variant-busy-styles')) return;
    const style = document.createElement('style');
    style.id = 'lumea-moonbundle-variant-busy-styles';
    style.textContent = `
      moonbundle-cart-drawer-block[data-lumea-variant-change-pending] select.moonbundle-select__select {
        pointer-events: none !important;
        cursor: wait !important;
        opacity: .62 !important;
      }
    `;
    document.head.appendChild(style);
  };

  const changeCartItemVariant = async ({
    itemKey,
    selectedOptions,
    displayedLineKeys,
    sourceSelect,
  }) => {
    const drawer = document.querySelector('moonbundle-cart-drawer-block');

    try {
      drawer?.enableLoading?.('changeVariant', itemKey);
      const cart = await requestCart('cart.js');
      const item = cart.items.find((cartItem) => cartItem.key === itemKey);

      if (!item) throw new Error('Cart line not found');

      if (!isMoonBundleProduct(item.properties)) {
        drawer?.handleChangeSelectVariantFromSelect?.(sourceSelect);
        return;
      }

      const product = await getProduct(item);
      const optionNames = product.options.map((option) =>
        typeof option === 'string' ? option : option.name
      );
      const variant = product.variants.find((candidate) =>
        candidate.options.every(
          (value, index) => selectedOptions[optionNames[index]] === value
        )
      );

      if (!variant?.available) throw new Error('Variant unavailable');

      const replacement = {
        id: variant.id,
        quantity: item.quantity,
        properties: {
          ...item.properties,
          _lumea_bundle_unit: item.properties?._lumea_bundle_unit || createUnitId(),
        },
      };
      const preferredOrder = getPreferredCartLineOrder(
        cart,
        displayedLineKeys,
        item,
        replacement
      );
      const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;

      if (sellingPlanId) replacement.selling_plan = sellingPlanId;

      await requestCart('cart/change.js', 'POST', { id: item.key, quantity: 0 });

      try {
        await requestCart('cart/add.js', 'POST', { items: [replacement] });
      } catch (error) {
        const rollback = {
          id: item.variant_id || item.id,
          quantity: item.quantity,
          properties: item.properties,
        };

        if (sellingPlanId) rollback.selling_plan = sellingPlanId;
        await requestCart('cart/add.js', 'POST', { items: [rollback] }).catch(() => {});
        throw error;
      }

      const updatedCart = await requestCart('cart.js');
      refreshMoonBundleDrawer(updatedCart, preferredOrder);
    } catch (error) {
      console.error('[Lumea] Unable to change this MoonBundle variant:', error);
      const cart = await requestCart('cart.js').catch(() => null);
      if (cart) refreshMoonBundleDrawer(cart);
      drawer?.svelteApp?.showVariantOutOfStockFlash?.(itemKey);
    } finally {
      drawer?.disableLoading?.('changeVariant', itemKey);
    }
  };

  let normalizationTimer;
  scheduleNormalization = (delay = 150) => {
    window.clearTimeout(normalizationTimer);
    normalizationTimer = window.setTimeout(normalizeExistingCart, delay);
  };

  let variantChangePromise = null;

  const queueCartItemVariantChange = (select) => {
    const drawer = select.closest('moonbundle-cart-drawer-block');
    if (!drawer || variantChangePromise) return variantChangePromise;

    const itemKey = select.dataset.itemKey;
    const selectedOptions = Object.fromEntries(
      [...drawer.querySelectorAll('select.moonbundle-select__select')]
        .filter((optionSelect) => optionSelect.dataset.itemKey === itemKey)
        .map((optionSelect) => [optionSelect.dataset.optionName, optionSelect.value])
    );
    const displayedLineKeys = getDisplayedCartLineKeys(drawer);

    setVariantControlsBusy(drawer, true);
    variantChangePromise = changeCartItemVariant({
      itemKey,
      selectedOptions,
      displayedLineKeys,
      sourceSelect: select,
    }).finally(() => {
      setVariantControlsBusy(drawer, false);
      variantChangePromise = null;
      window.__lumeaCartVariantChangePromise = null;
    });
    window.__lumeaCartVariantChangePromise = variantChangePromise;
    return variantChangePromise;
  };

  window.__lumeaNormalizeMoonBundleCart = normalizeExistingCart;
  installBusyStyles();
  document.addEventListener(
    'change',
    (event) => {
      const select = event.target.closest?.('select.moonbundle-select__select');
      const drawer = select?.closest('moonbundle-cart-drawer-block');

      if (!select || !drawer) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      queueCartItemVariantChange(select);
    },
    true
  );
  document.addEventListener('ajax-cart:added', () => scheduleNormalization());
  window.addEventListener('moonbundle-cart-updated', (event) => {
    if (event.detail?.caller !== 'lumea-unit-normalizer') scheduleNormalization();
  });
  window.addEventListener('pageshow', () => scheduleNormalization());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleNormalization(), { once: true });
  } else {
    scheduleNormalization();
  }
})();
